import { capturePrivateSession, assertPrivateSessionLifetime, getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import { useRef, useState, useSyncExternalStore, type FormEvent } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Info, KeyRound, LoaderCircle, Lock } from "lucide-react";
import { changePrivateSession } from "@/shared/privateSessionChange";
import { apiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { Screen } from "@/shared/ui";
import { AuthField } from "./login/AuthField";
import { AuthShell } from "./login/AuthShell";
import { useLogout } from "./useLogout";
import { useSession } from "./useSession";

const MIN_PASSWORD = 8;

/**
 * A TEMPORARY PASSWORD WORKS ONCE: the member picks their own before the app
 * opens (claude.ai/design "Login", the second step). On success the session
 * ends and the login screen says the password was saved (`?saved=1`).
 */
export function FirstLoginPasswordPage() {
  const lifetime = useRef(capturePrivateSession()).current;
  const t = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const identity = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  const { data: replacementFailed } = useQuery<boolean>({
    queryKey: ["first-password-feedback", identity.generation],
    queryFn: () => false, enabled: false,
  });
  const { locale = "ar" } = useParams();
  const { data, isLoading, error } = useSession();
  const { logout, isLoggingOut } = useLogout();
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<"short" | "differ" | null>(null);
  const [saving, setSaving] = useState(false);

  if (isLoading) return <Screen title={t((m) => m.common.loading)}><div /></Screen>;
  if (error || !data) return <Navigate to={`/${locale}/login`} replace />;
  if (!data.mustChangePassword) return <Navigate to={`/${locale}`} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving || identity.changing) return;
    assertPrivateSessionLifetime(lifetime);
    if (nextPassword.length < MIN_PASSWORD) {
      setLocalError("short");
      return;
    }
    if (nextPassword !== confirmPassword) {
      setLocalError("differ");
      return;
    }
    setLocalError(null);
    const owner = getPrivateSessionState();
    const generation = owner.generation + 1;
    queryClient.removeQueries({ queryKey: ["first-password-feedback"] });
    setSaving(true);
    try {
      await changePrivateSession(queryClient, async () => {
        await apiFetch(endpoints.seller.password, {
          method: "PATCH",
          body: { currentPassword, nextPassword },
          sessionChange: true,
        });
        await apiFetch(endpoints.seller.logout, { method: "POST" });
      });
      if (getPrivateSessionState().generation !== lifetime.generation + 1) return;
      queryClient.clear();
      navigate(`/${locale}/login?saved=1`, { replace: true });
    } catch {
      const current = getPrivateSessionState();
      if (!current.changing && current.generation === generation &&
        current.actorId === owner.actorId && current.storeSlug === owner.storeSlug) {
        queryClient.setQueryData(["first-password-feedback", generation], true);
      }
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || identity.changing;
  const strong = nextPassword.length >= MIN_PASSWORD;
  const errorText =
    localError === "short"
      ? t((m) => m.auth.newPasswordTooShort)
      : localError === "differ"
        ? t((m) => m.auth.newPasswordsDiffer)
        : replacementFailed
          ? t((m) => m.auth.firstPasswordFailed)
          : null;
  const clearError = () => setLocalError(null);

  return (
    <AuthShell subtitleRef={subtitleRef}>
      <span className="auth-hero">
        <DoodleBadge id="key" size={56} />
      </span>
      <h1 className="auth-title">{t((m) => m.auth.setOwnPassword)}</h1>
      <span ref={subtitleRef} className="auth-sub">
        {t((m) => m.auth.setOwnPasswordSub)}
      </span>
      <form className="auth-form" onSubmit={submit}>
        <AuthField
          label={t((m) => m.auth.temporaryPassword)}
          icon={KeyRound}
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          required
          invalid={Boolean(errorText)}
          hint={t((m) => m.auth.temporaryPasswordHint)}
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            clearError();
          }}
        />
        <AuthField
          label={t((m) => m.auth.newPassword)}
          icon={Lock}
          name="nextPassword"
          type="password"
          autoComplete="new-password"
          required
          invalid={Boolean(errorText)}
          hint={
            nextPassword
              ? strong
                ? t((m) => m.auth.atLeastEightMet)
                : t(
                    countedKey(MIN_PASSWORD - nextPassword.length, {
                      one: (m) => m.auth.moreCharactersOne,
                      two: (m) => m.auth.moreCharactersTwo,
                      few: (m) => m.auth.moreCharactersFew,
                      many: (m) => m.auth.moreCharacters,
                    }),
                    { count: MIN_PASSWORD - nextPassword.length },
                  )
              : t((m) => m.auth.atLeastEight)
          }
          hintTone={nextPassword ? (strong ? "ok" : "warn") : undefined}
          value={nextPassword}
          onChange={(event) => {
            setNextPassword(event.target.value);
            clearError();
          }}
        />
        <AuthField
          label={t((m) => m.auth.confirmPassword)}
          icon={Lock}
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          invalid={Boolean(errorText)}
          hint={confirmPassword && confirmPassword !== nextPassword ? t((m) => m.auth.notMatchingYet) : undefined}
          hintTone="error"
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            clearError();
          }}
        />
        {errorText ? (
          <div className="auth-note" data-tone="error" role="alert">
            <CircleAlert size={16} strokeWidth={1.8} color="rgb(212,76,71)" aria-hidden />
            <span>{errorText}</span>
          </div>
        ) : null}
        <button type="submit" className="auth-submit" disabled={busy} data-testid="first-password-submit">
          {busy ? (
            <span className="auth-spin">
              <LoaderCircle size={16} strokeWidth={2} aria-hidden />
            </span>
          ) : null}
          <span>{t((m) => (busy ? m.auth.saving : m.auth.saveOwnPassword))}</span>
        </button>
      </form>
      <div className="auth-foot">
        <Info size={15} strokeWidth={1.8} aria-hidden />
        <span>{t((m) => m.auth.afterSaving)}</span>
      </div>
      {/* Leaving this step signs the temporary session out: the app will not
          open on a temporary password. */}
      <button
        type="button"
        className="auth-back"
        disabled={isLoggingOut}
        onClick={logout}
        data-testid="first-password-back"
      >
        {t((m) => m.auth.backToLogin)}
      </button>
    </AuthShell>
  );
}
